import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SectionReveal from '../components/SectionReveal';

/* SVG icons for service cards */
const icons = {
  custom: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  speed: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  integrate: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  evolve: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
};

const services = [
  { icon: icons.custom, title: 'Bespoke Builds', desc: 'Precision-engineered AI tools molded to your exact workflow — not the other way around.' },
  { icon: icons.speed, title: 'Instant Impact', desc: 'Eliminate repetitive manual work and unlock speed without sacrificing quality.' },
  { icon: icons.integrate, title: 'Seamless Integration', desc: 'Drops into your existing systems and tech stack with zero disruption.' },
  { icon: icons.evolve, title: 'Adaptive Intelligence', desc: 'Solutions that evolve alongside your processes as your business scales.' },
];

const Home = () => {
  return (
    <>
      {/* ───── HERO ───── */}
      <section className="hero">
        <div className="hero__bg-grid" />
        <div className="container hero__inner">
          <motion.div
            className="hero__badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero__badge-dot" />
            Custom AI Solutions
          </motion.div>

          <motion.h1
            className="hero__title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            Built For the Way<br />
            <span className="gradient-text">You</span> Work
          </motion.h1>

          <motion.p
            className="hero__sub"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            Tailored AI Software for Any Industry, Any Workflow.
          </motion.p>

          <motion.p
            className="hero__powered"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            Powered by <span className="dell-blue">Dell Pro Max</span> & <span className="nvidia-green">NVIDIA RTX PRO GPUs</span>
          </motion.p>

          <motion.div
            className="hero__actions"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
          >
            <Link to="/contact" className="btn btn--primary btn--lg">
              Book Your Dream Call
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </Link>
            <Link to="/about" className="btn btn--ghost btn--lg">
              Learn More
            </Link>
          </motion.div>

          {/* Floating stat badges */}
          <motion.div
            className="hero__stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            <div className="stat-pill">
              <span className="stat-pill__number">30s</span>
              <span className="stat-pill__label">Script Analysis</span>
            </div>
            <div className="stat-pill">
              <span className="stat-pill__number">100%</span>
              <span className="stat-pill__label">Custom-Built</span>
            </div>
            <div className="stat-pill">
              <span className="stat-pill__number">0</span>
              <span className="stat-pill__label">Off-the-Shelf</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───── SERVICES ───── */}
      <section className="section services" id="services">
        <div className="container">
          <SectionReveal>
            <p className="section__eyebrow">What We Do</p>
            <h2 className="section__title">The Full Speed AI Difference</h2>
            <p className="section__subtitle">
              We don't sell software — we craft solutions. Custom AI that fits you, not the other way around.
            </p>
          </SectionReveal>

          <div className="services__grid">
            {services.map((svc, i) => (
              <SectionReveal key={i} delay={i * 0.1}>
                <div className="card">
                  <div className="card__icon">{svc.icon}</div>
                  <h3 className="card__title">{svc.title}</h3>
                  <p className="card__desc">{svc.desc}</p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───── SPEED READ AI (Case Study) ───── */}
      <section className="section case-study">
        <div className="container">
          <div className="case-study__wrapper">
            <SectionReveal className="case-study__content">
              <p className="section__eyebrow">Case Study</p>
              <img src="/images/logos/speed-read-ai.png" alt="Speed Read AI" className="case-study__logo" />
              <p className="case-study__tag">Revolutionizing Script Evaluation in Entertainment</p>
              <p className="case-study__body">
                Speed Read AI is our flagship product — purpose-built to solve a longstanding industry
                challenge: the time and effort it takes to read and evaluate screenplays and manuscripts.
              </p>
              <p className="case-study__body">
                Designed for writers, production companies, film studios, and talent agencies, it
                analyzes a full-length script or book in just 30–45 seconds. It surfaces key story
                elements, evaluates commercial viability, and lets users ask questions about the
                material — turning a time-consuming task into an actionable insight session.
              </p>
              <p className="case-study__body case-study__body--highlight">
                In an industry where most scripts go unread, Speed Read AI levels the playing field —
                ensuring every story has a chance to be seen, heard, and considered.
              </p>
            </SectionReveal>

            <SectionReveal className="case-study__visual" delay={0.2}>
              <div className="case-study__card">
                <div className="case-study__card-header">
                  <div className="case-study__dots">
                    <span /><span /><span />
                  </div>
                  <span className="case-study__card-label">Speed Read AI</span>
                </div>
                <div className="case-study__card-body">
                  <div className="case-study__metric">
                    <span className="case-study__metric-value">30-45s</span>
                    <span className="case-study__metric-label">Full script analysis</span>
                  </div>
                  <div className="case-study__metric">
                    <span className="case-study__metric-value">Key Elements</span>
                    <span className="case-study__metric-label">Story structure surfaced</span>
                  </div>
                  <div className="case-study__metric">
                    <span className="case-study__metric-value">Ask Anything</span>
                    <span className="case-study__metric-label">Interactive Q&A on material</span>
                  </div>
                  <div className="case-study__bar-group">
                    <div className="case-study__bar">
                      <span>Commercial Viability</span>
                      <div className="case-study__bar-track"><div className="case-study__bar-fill" style={{ width: '87%' }} /></div>
                    </div>
                    <div className="case-study__bar">
                      <span>Story Originality</span>
                      <div className="case-study__bar-track"><div className="case-study__bar-fill" style={{ width: '92%' }} /></div>
                    </div>
                    <div className="case-study__bar">
                      <span>Character Depth</span>
                      <div className="case-study__bar-track"><div className="case-study__bar-fill" style={{ width: '78%' }} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ───── PARTNERS ───── */}
      <section className="section partners">
        <div className="container">
          <div className="partners__content">
            <SectionReveal className="partners__text">
              <h2>Powered by Dell Technologies &amp; NVIDIA RTX Pro GPUs</h2>
              <p>
                By partnering with Dell Technologies and NVIDIA, Full Speed AI is able to deliver
                on-prem <em>and</em> cloud-based solutions custom tailored to the exact needs of your business.
              </p>
            </SectionReveal>

            <SectionReveal className="partners__logos" delay={0.2}>
              <div className="partner-card">
                <img src="/images/logos/dell-technologies.png" alt="Dell Technologies" className="partner-card__img partner-card__img--dell" />
              </div>
              <div className="partner-card">
                <img src="/images/logos/nvidia.png" alt="NVIDIA" className="partner-card__img partner-card__img--nvidia" />
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* ───── CTA ───── */}
      <section className="section cta">
        <div className="container">
          <SectionReveal>
            <div className="cta__box">
              <div className="cta__glow" />
              <p className="cta__eyebrow">The Time Is Now</p>
              <h2 className="cta__title">Ready to Build Something <span className="gradient-text">Extraordinary</span>?</h2>
              <p className="cta__sub">
                Book your free AI Dream Call and take the first step toward transforming the way your organization works.
              </p>
              <Link to="/contact" className="btn btn--primary btn--lg">
                Book Your Dream Call
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </Link>
            </div>
          </SectionReveal>
        </div>
      </section>
    </>
  );
};

export default Home;
